import { RingBuffer } from "ring-buffer-ts";

import { TrackingFrame } from "mannequin/src/types/tracking/mannequin";

import { nearestFrameIndex } from "../../lib/misc/util";

export type TaggedNeighbor<T> =
  | { neighbor: T; time: number; num: number }
  | undefined;

export type TaggedNeighborPair<T> = {
  prev: TaggedNeighbor<T>;
  next: TaggedNeighbor<T>;
};

export class Frames {
  private buffer?: RingBuffer<TrackingFrame>;

  private indices: {
    missed?: number;
    current?: number;
    max?: number;
  } = {};

  private prevFrameNum?: number;
  private maxMissedFrames: number;

  constructor(maxMissedFrames = 300) {
    this.maxMissedFrames = maxMissedFrames;
  }

  init(buffer: RingBuffer<TrackingFrame>) {
    this.buffer = buffer;
  }

  update(time?: number) {
    if (!this.buffer) return;

    if (time == undefined) {
      const last = this.buffer.getLast();
      if (last) {
        time = last.time;
      } else {
        console.error("empty tracking buffer - can't get latest");
        return frames;
      }
    }

    this.indices.current = nearestFrameIndex(this.buffer, time);
    let frameNum = undefined;

    if (this.prevFrameNum == undefined) {
      this.indices.missed = 0;
    }

    if (this.indices.current != undefined) {
      // find index since last call
      frameNum = this.buffer.get(this.indices.current)?.num;
      if (this.prevFrameNum != undefined && frameNum != undefined) {
        const numMissedFrames = frameNum - (this.prevFrameNum + 1);
        if (numMissedFrames >= 0) {
          this.indices.missed =
            this.indices.current -
            Math.min(numMissedFrames, this.maxMissedFrames);
        }
      }

      this.indices.max = this.buffer.getBufferLength() - 1;
    } else {
      console.error("invalid ceiling in binary search", time);
    }

    this.prevFrameNum = frameNum;

    return frames;
  }

  get current(): TrackingFrame | undefined {
    return this.buffer && this.indices.current != undefined
      ? this.buffer.get(this.indices.current)
      : undefined;
  }

  get next(): TrackingFrame | undefined {
    return this.buffer &&
      this.indices.current != undefined &&
      this.indices.max != undefined &&
      this.indices.current + 1 <= this.indices.max
      ? this.buffer.get(this.indices.current + 1)
      : undefined;
  }

  // TODO: see if we can use iterator / generator syntax to support for x of missed and
  //       not recreate array
  get missed(): TrackingFrame[] {
    const missed: TrackingFrame[] = [];

    if (
      !this.buffer ||
      this.indices.missed == undefined ||
      this.indices.current == undefined
    ) {
      return missed;
    }

    for (let i = this.indices.missed; i < this.indices.current; i++) {
      const frame = this.buffer.get(i);
      if (frame) missed.push(frame);
    }
    return missed;
  }

  /**
   * Get neighbouring frames which return something from the passed `extract`
   * function
   * @param extract
   * @returns
   */
  getNeighbours<T>(
    extract: (frame: TrackingFrame) => T | undefined
  ): TaggedNeighborPair<T> | undefined {
    if (
      !this.buffer ||
      this.indices.current == undefined ||
      this.indices.max == undefined
    ) {
      return undefined;
    }

    let prev: TaggedNeighbor<T> = undefined;
    let next: TaggedNeighbor<T> = undefined;
    for (let i = this.indices.current; i >= 0; i--) {
      const frame = this.buffer.get(i);
      if (!frame) continue;

      const query = extract(frame);
      if (query != undefined) {
        prev = { neighbor: query, time: frame.time, num: frame.num };
        break;
      }
    }

    for (let i = this.indices.current + 1; i <= this.indices.max; i++) {
      const frame = this.buffer.get(i);
      if (!frame) continue;

      const query = extract(frame);
      if (query != undefined) {
        next = { neighbor: query, time: frame.time, num: frame.num };
        break;
      }
    }

    return { prev, next };
  }
}
